/**
 * RepConnect - Sales Representative Provider Library Management
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 *
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import { logger } from './logger';

const PROVIDER_LIBRARY_KEY = 'meetingsync_provider_library';
const PROVIDER_LISTS_KEY = 'meetingsync_provider_lists';

export const ProviderLibrary = {
  // Get all saved providers for a sales rep
  getSavedProviders(salesRepId) {
    try {
      const library = localStorage.getItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`);
      return library ? JSON.parse(library) : [];
    } catch (error) {
      logger.error('Error getting saved providers', { error: error.message, salesRepId });
      return [];
    }
  },

  // Save a provider to the library
  saveProvider(salesRepId, provider, listIds = []) {
    try {
      const savedProviders = this.getSavedProviders(salesRepId);
      
      // Check if provider already exists
      const existingIndex = savedProviders.findIndex(p => p.id === provider.id);
      
      const savedProvider = {
        ...provider,
        savedAt: new Date().toISOString(),
        listIds: listIds || [],
        notes: '',
        tags: [],
        priority: 'normal', // low, normal, high
        lastContactDate: null,
        nextFollowUp: null
      };

      if (existingIndex >= 0) {
        // Update existing provider but preserve user data
        savedProviders[existingIndex] = {
          ...savedProvider,
          notes: savedProviders[existingIndex].notes,
          tags: savedProviders[existingIndex].tags,
          priority: savedProviders[existingIndex].priority,
          lastContactDate: savedProviders[existingIndex].lastContactDate,
          nextFollowUp: savedProviders[existingIndex].nextFollowUp,
          savedAt: savedProviders[existingIndex].savedAt // Keep original save date
        };
      } else {
        savedProviders.push(savedProvider);
      }

      localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(savedProviders));
      return savedProvider;
    } catch (error) {
      logger.error('Error saving provider', { error: error.message, salesRepId, providerId: provider.id });
      return null;
    }
  },

  // Remove a provider from the library
  removeProvider(salesRepId, providerId) {
    try {
      const savedProviders = this.getSavedProviders(salesRepId);
      const filteredProviders = savedProviders.filter(p => p.id !== providerId);
      localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(filteredProviders));
      return true;
    } catch (error) {
      logger.error('Error removing provider', { error: error.message, salesRepId, providerId });
      return false;
    }
  },

  // Update provider details (notes, tags, priority, etc.)
  updateProvider(salesRepId, providerId, updates) {
    try {
      const savedProviders = this.getSavedProviders(salesRepId);
      const providerIndex = savedProviders.findIndex(p => p.id === providerId);
      
      if (providerIndex >= 0) {
        savedProviders[providerIndex] = {
          ...savedProviders[providerIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
        
        localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(savedProviders));
        return savedProviders[providerIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating provider', { error: error.message, salesRepId, providerId });
      return null;
    }
  },

  // Check if a provider is saved
  isProviderSaved(salesRepId, providerId) {
    const savedProviders = this.getSavedProviders(salesRepId);
    return savedProviders.some(p => p.id === providerId);
  },

  // Get provider lists (categories/folders)
  getProviderLists(salesRepId) {
    try {
      const lists = localStorage.getItem(`${PROVIDER_LISTS_KEY}_${salesRepId}`);
      const defaultLists = [
        { id: 'favorites', name: 'Favorites', color: 'red', isDefault: true },
        { id: 'prospects', name: 'Prospects', color: 'blue', isDefault: true },
        { id: 'active', name: 'Active Contacts', color: 'green', isDefault: true },
        { id: 'follow-up', name: 'Follow Up', color: 'orange', isDefault: true }
      ];


      return lists ? [...defaultLists, ...JSON.parse(lists)] : defaultLists;
    } catch (error) {
      logger.error('Error getting provider lists', { error: error.message, salesRepId });
      return [];
    }
  },

  // Create a new provider list
  createProviderList(salesRepId, listName, color = 'gray') {
    try {
      const lists = this.getProviderLists(salesRepId).filter(l => !l.isDefault);
      const newList = {
        id: Date.now().toString(),
        name: listName,
        color: color,
        createdAt: new Date().toISOString(),
        isDefault: false
      };


      lists.push(newList);
      localStorage.setItem(`${PROVIDER_LISTS_KEY}_${salesRepId}`, JSON.stringify(lists));
      return newList;
    } catch (error) {
      logger.error('Error creating provider list', { error: error.message, salesRepId, listName });
      return null;
    }
  },

  // Update a provider list
  updateProviderList(salesRepId, listId, updates) {
    try {
      const lists = this.getProviderLists(salesRepId).filter(l => !l.isDefault);
      const listIndex = lists.findIndex(l => l.id === listId);
      
      if (listIndex >= 0) {
        lists[listIndex] = { ...lists[listIndex], ...updates };
        localStorage.setItem(`${PROVIDER_LISTS_KEY}_${salesRepId}`, JSON.stringify(lists));
        return lists[listIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating provider list', { error: error.message, salesRepId, listId });
      return null;
    }
  },

  // Delete a provider list
  deleteProviderList(salesRepId, listId) {
    try {
      const lists = this.getProviderLists(salesRepId).filter(l => !l.isDefault && l.id !== listId);
      localStorage.setItem(`${PROVIDER_LISTS_KEY}_${salesRepId}`, JSON.stringify(lists));
      
      // Remove list from all providers
      const savedProviders = this.getSavedProviders(salesRepId);
      const updatedProviders = savedProviders.map(provider => ({
        ...provider,
        listIds: provider.listIds.filter(id => id !== listId)
      }));
      
      localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(updatedProviders));
      return true;
    } catch (error) {
      logger.error('Error deleting provider list', { error: error.message, salesRepId, listId });
      return false;
    }
  },

  // Add provider to lists
  addProviderToLists(salesRepId, providerId, listIds) {
    try {
      const savedProviders = this.getSavedProviders(salesRepId);
      const providerIndex = savedProviders.findIndex(p => p.id === providerId);
      
      if (providerIndex >= 0) {
        const currentListIds = savedProviders[providerIndex].listIds || [];
        const newListIds = [...new Set([...currentListIds, ...listIds])];
        
        savedProviders[providerIndex].listIds = newListIds;
        localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(savedProviders));
        return savedProviders[providerIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error adding provider to lists', { error: error.message, salesRepId, providerId });
      return null;
    }
  },

  // Remove provider from lists
  removeProviderFromLists(salesRepId, providerId, listIds) {
    try {
      const savedProviders = this.getSavedProviders(salesRepId);
      const providerIndex = savedProviders.findIndex(p => p.id === providerId);
      
      if (providerIndex >= 0) {
        const currentListIds = savedProviders[providerIndex].listIds || [];
        const newListIds = currentListIds.filter(id => !listIds.includes(id));
        
        savedProviders[providerIndex].listIds = newListIds;
        localStorage.setItem(`${PROVIDER_LIBRARY_KEY}_${salesRepId}`, JSON.stringify(savedProviders));
        return savedProviders[providerIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error removing provider from lists', { error: error.message, salesRepId, providerId });
      return null;
    }
  },

  // Get providers by list
  getProvidersByList(salesRepId, listId) {
    const savedProviders = this.getSavedProviders(salesRepId);
    return savedProviders.filter(provider => 
      provider.listIds && provider.listIds.includes(listId)
    );
  },

  // Search saved providers
  searchSavedProviders(salesRepId, searchTerm, filters = {}) {
    const savedProviders = this.getSavedProviders(salesRepId);
    let filtered = savedProviders;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(provider =>
        provider.name.toLowerCase().includes(term) ||
        provider.specialty.toLowerCase().includes(term) ||
        provider.hospital.toLowerCase().includes(term) ||
        (provider.notes && provider.notes.toLowerCase().includes(term)) ||
        (provider.tags && provider.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    // Filter by specialty
    if (filters.specialty && filters.specialty !== 'all') {
      filtered = filtered.filter(provider => 
        provider.specialty.toLowerCase() === filters.specialty.toLowerCase()
      );
    }

    // Filter by priority
    if (filters.priority && filters.priority !== 'all') {
      filtered = filtered.filter(provider => provider.priority === filters.priority);
    }

    // Filter by list
    if (filters.listId) {
      filtered = filtered.filter(provider => 
        provider.listIds && provider.listIds.includes(filters.listId)
      );
    }

    return filtered;
  },

  // Get provider statistics
  getProviderStats(salesRepId) {
    const savedProviders = this.getSavedProviders(salesRepId);
    const lists = this.getProviderLists(salesRepId);
    
    const stats = {
      total: savedProviders.length,
      byPriority: {
        high: savedProviders.filter(p => p.priority === 'high').length,
        normal: savedProviders.filter(p => p.priority === 'normal').length,
        low: savedProviders.filter(p => p.priority === 'low').length
      },
      bySpecialty: {},
      byList: {},
      recentlyAdded: savedProviders.filter(p => {
        const savedDate = new Date(p.savedAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return savedDate >= weekAgo;
      }).length
    };

    // Count by specialty
    savedProviders.forEach(provider => {
      stats.bySpecialty[provider.specialty] = (stats.bySpecialty[provider.specialty] || 0) + 1;
    });

    // Count by list
    lists.forEach(list => {
      stats.byList[list.id] = this.getProvidersByList(salesRepId, list.id).length;
    });

    return stats;
  },

  // Export providers to CSV
  exportProviders(salesRepId) {
    const savedProviders = this.getSavedProviders(salesRepId);
    const lists = this.getProviderLists(salesRepId);
    
    const csvData = savedProviders.map(provider => ({
      Name: provider.name,
      Specialty: provider.specialty,
      Hospital: provider.hospital,
      Phone: provider.phone,
      Email: provider.email,
      Address: provider.address,
      Rating: provider.rating,
      'Meetings Completed': provider.meetingsCompleted,
      'Preferred Time': provider.preferredTime,
      Priority: provider.priority,
      Notes: provider.notes,
      Tags: provider.tags ? provider.tags.join(', ') : '',
      Lists: provider.listIds ? provider.listIds.map(id => {
        const list = lists.find(l => l.id === id);
        return list ? list.name : id;
      }).join(', ') : '',
      'Saved Date': provider.savedAt,
      'Last Contact': provider.lastContactDate || '',
      'Next Follow Up': provider.nextFollowUp || ''
    }));

    return csvData;
  }
};